import * as React from "react";
import { useSelector, useDispatch } from "react-redux";
import { bindActionCreators } from "redux";
import ConfirmationModal from "../../components/ConfirmationModal/ConfirmationModal";
import NavigationLink from "../../components/NavigationLink/NavigationLink";
import { editActions } from "../../actions/editActions";
import { pdApiActions } from "../../actions/pdApiActions";
import { columnActions } from "../../actions/columnActions";
import {
  editedObjectIdSelector,
  isEditActiveSelector,
  editedObjectIdentifierSelector,
  domainSelector
} from "../../reducers/editReducer";
import { NetworkFaultsTable } from "./Faults/NetworkFaultsTable";
import {
  setFaultExtraProperties,
  createEmptyFault
} from "../../utils/pdApiUtils";
import { ActionMenu } from "../common/ActionMenu/ActionMenu";
import ContentLayout from "../../components/ContentLayout/ContentLayout";
import { mainActions } from "../../actions/mainActions";
import { Filter } from "../../components/Table/Migration/LegacyTypes";

type OwnProps = {
  subMenuRoute: State.NetworkSubmenu;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  match: any;
  userFiltering?: Filter[];
};

// Props of the component
export type NetworkProps = OwnProps;

const groupName: Api.GroupName = "Network";

/**
 * To add new sub page to this parent, add it here
 */
const subPages: Internal.SubPages = {
  "Network Faults": {
    itemName: "fault",
    PdApiField: "networkFaults",
    dataView: NetworkFaultsTable,
    createEmpty: createEmptyFault,
    setExtraProperties: setFaultExtraProperties
  }
};

export const Network: React.FC<NetworkProps> = (props) => {
  const { subMenuRoute } = props;
  
  const dispatch = useDispatch();

  
  // Map state from Redux store
  const pdApi = useSelector((state: State.Root) => state.pdApi);
  const fetchingStatus = useSelector((state: State.Root) => state.main.fetchingStatus);
  const columns = useSelector((state: State.Root) => state.columns);
  const mainState = useSelector((state: State.Root) => state.main);
  const isLoadingFaults = useSelector((state: State.Root) => state.loadingStatus.isNetworkFaultsLoading);
  const isEditActive = useSelector((state: State.Root) => isEditActiveSelector(state.editView));
  const editedObjectId = useSelector((state: State.Root) => editedObjectIdSelector(state.editView));
  const objectIdentifier = useSelector((state: State.Root) => editedObjectIdentifierSelector(state.editView, subMenuRoute));
  const domainName = useSelector((state: State.Root) => domainSelector(state.editView));
  const toggleChildView = useSelector((state: State.Root) => state.toggleChildView);
  const filterOptions = useSelector((state: State.Root) => state.filterOptions);
  
  // Actions - bind all action creators with dispatch
  const editActionsDispatched = React.useMemo(() => bindActionCreators(editActions, dispatch), [dispatch]);
  const pdApiActionsDispatched = React.useMemo(() => bindActionCreators(pdApiActions, dispatch), [dispatch]);
  const mainActionsDispatched = React.useMemo(() => bindActionCreators(mainActions, dispatch), [dispatch]);
  const columnActionsDispatched = React.useMemo(() => bindActionCreators(columnActions, dispatch), [dispatch]);
  
  const [isModalOpen, setIsModalOpen] = React.useState(false);
  const [tableColumnFilters, setTableColumnFilters] = React.useState<{
    [key in State.NetworkSubmenu]: Filter[];
  }>({
    "Network Faults": []
  });

  React.useEffect(() => {
    if (
      pdApi.networkFaults === undefined &&
      window.location.href.includes("network/faults")
    ) {
      pdApiActionsDispatched.fetchNetworkFaults();
    }
  }, [pdApi.networkFaults, pdApiActionsDispatched]);

  const setColumnFilters = React.useCallback((subMenuRoute: State.NetworkSubmenu, newFilters: Filter[]): void => {
    setTableColumnFilters(prevFilters => ({
      ...prevFilters,
      [subMenuRoute]: newFilters
    }));
  }, []);

  const toggleModal = React.useCallback((): void => {
    setIsModalOpen(prev => !prev);
  }, []);

  const buildLink = React.useCallback((subPageName: State.NetworkSubmenu): JSX.Element => {
    // To dynamically build urls we use the itenName and make it plural, so -s || -y => -ies
    let parsedLink = subPages[subPageName].itemName.replace(" ", "-");
    parsedLink = parsedLink.slice(-1) === "y" ? parsedLink.slice(0, -1) + "ies" : parsedLink + "s";
    
    const handleLinkClick = () => {
      editActionsDispatched.cancelEditing();
      if (subPageName === "Network Faults" && pdApi.networkFaults === undefined) {
        pdApiActionsDispatched.fetchNetworkFaults();
      }
    };

    return (
      <NavigationLink
        key={subPageName}
        handleOnClick={handleLinkClick}
        name={subPageName}
        to={"/network/" + parsedLink + "/"}
      />
    );
  }, [editActionsDispatched, pdApiActionsDispatched, pdApi.networkFaults]);

  const getDataView = React.useCallback((subMenuRoute: State.NetworkSubmenu): JSX.Element => {
    if (subMenuRoute in subPages) {
      const passProps = {
        pdApi,
        fetchingStatus,
        columns,
        mainState,
        editedObjectId,
        isEditActive,
        objectIdentifier,
        domainName,
        toggleChildView,
        isLoadingFaults,
        filterOptions,
        pdApiActions: pdApiActionsDispatched,
        columnActions: columnActionsDispatched,
        editActions: editActionsDispatched,
        mainActions: mainActionsDispatched,
        subMenuRoute,
        match: props.match,
        userFiltering: props.userFiltering,
        toggleModal,
        setColumnFilters,
        columnFilters: tableColumnFilters[subMenuRoute]
      };
      return React.createElement(subPages[subMenuRoute].dataView, passProps);
    }
    return <>Error</>;
  }, [pdApi, fetchingStatus, columns, mainState, editedObjectId, isEditActive, objectIdentifier, domainName, toggleChildView, isLoadingFaults, filterOptions, pdApiActionsDispatched, columnActionsDispatched, editActionsDispatched, mainActionsDispatched, subMenuRoute, props.match, props.userFiltering, toggleModal, setColumnFilters, tableColumnFilters]);

  const getActionMenu = React.useCallback((subMenuRoute: State.NetworkSubmenu): JSX.Element | null => {
    if (subPages[subMenuRoute].PdApiField === null) {
      return null;
    }
    return (
      <ActionMenu
        pdApi={pdApi}
        columns={columns}
        mainState={mainState}
        isEditActive={isEditActive}
        objectIdentifier={objectIdentifier}
        editActions={editActionsDispatched}
        columnActions={columnActionsDispatched}
        subMenuRoute={subMenuRoute}
        groupName={groupName}
        selectedColumnsName={subMenuRoute}
        dataFieldName={subPages[subMenuRoute].PdApiField}
        setExtraProperties={subPages[subMenuRoute].setExtraProperties}
        createEmpty={subPages[subMenuRoute].createEmpty}
        itemName={subPages[subMenuRoute].itemName}
        columnFilters={tableColumnFilters[subMenuRoute]}
        filterOptions={filterOptions}
      />
    );
  }, [pdApi, columns, mainState, isEditActive, objectIdentifier, editActionsDispatched, columnActionsDispatched, subMenuRoute, tableColumnFilters, filterOptions]);

  const buildModal = React.useCallback(() => {
    const domainNameText = domainName ? " Domain: " + domainName : "";
    const content = (
      <span>
        Are you sure you want to delete this {subPages[subMenuRoute].itemName}?
        <br />
        {objectIdentifier}
        <br /> {domainNameText}
      </span>
    );

    return (
      <ConfirmationModal
        isOpen={isModalOpen}
        size="mini"
        header="Confirm delete"
        content={content}
        confirmText="Confirm"
        cancelText="Cancel"
        onConfirm={() => {
          editActionsDispatched.delete(
            subPages[subMenuRoute].PdApiField as Api.OptionsGrud
          );
        }}
        onCancel={toggleModal}
      />
    );
  }, [isModalOpen, subMenuRoute, objectIdentifier, domainName, editActionsDispatched, toggleModal]);

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const submenuLinks = Object.keys(subPages).map((subPageName: any) =>
    buildLink(subPageName)
  );
  const actionMenu = getActionMenu(subMenuRoute);
  const dataView = getDataView(subMenuRoute);
  const modal = subPages[subMenuRoute].PdApiField ? buildModal() : null;

  return (
    <>
      <ContentLayout submenu={submenuLinks} actionMenu={actionMenu} dataView={dataView} />
      {modal}
    </>
  );
};

export default Network;
